import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configApi, type StatusConfig } from '../api/config';
import type { StateInfo } from '../types';

const ENTITY_LABELS: Record<string, string> = {
  anfrage: 'Anfragen',
  inserat: 'Inserate',
  verfahren: 'Verfahren',
};

const AVAILABLE_COLORS = [
  { value: 'blue',   label: 'Blau' },
  { value: 'cyan',   label: 'Cyan' },
  { value: 'teal',   label: 'Türkis' },
  { value: 'green',  label: 'Grün' },
  { value: 'yellow', label: 'Gelb' },
  { value: 'orange', label: 'Orange' },
  { value: 'red',    label: 'Rot' },
  { value: 'purple', label: 'Lila' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'gray',   label: 'Grau' },
];

const COLOR_CLASSES: Record<string, string> = {
  blue:   'bg-blue-100 text-blue-800',
  cyan:   'bg-cyan-100 text-cyan-800',
  teal:   'bg-teal-100 text-teal-800',
  green:  'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  orange: 'bg-orange-100 text-orange-800',
  red:    'bg-red-100 text-red-800',
  purple: 'bg-purple-100 text-purple-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  gray:   'bg-gray-100 text-gray-600',
};

interface StatusConfigEditorProps {
  /** If provided, edit this pre-loaded config instead of fetching it */
  initialConfig?: StatusConfig;
  /** Called with the updated config to persist it */
  onSave: (config: StatusConfig) => Promise<void>;
  /** Label shown on save button */
  saveLabel?: string;
}

export default function StatusConfigEditor({ initialConfig, onSave, saveLabel }: StatusConfigEditorProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'anfrage' | 'inserat' | 'verfahren'>('anfrage');
  const [localConfig, setLocalConfig] = useState<StatusConfig | null>(initialConfig ?? null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const { data: fetchedConfig, isLoading } = useQuery({
    queryKey: ['status-config'],
    queryFn: configApi.getStatusConfig,
    enabled: !initialConfig,
  });

  // Populate localConfig once the fetched data arrives (only if not controlled by initialConfig)
  const [seeded, setSeeded] = useState(false);
  if (fetchedConfig && !seeded && !initialConfig) {
    setLocalConfig(fetchedConfig);
    setSeeded(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (config: StatusConfig) => {
      await onSave(config);
      return config;
    },
    onSuccess: (saved) => {
      setLocalConfig(saved as StatusConfig);
      queryClient.setQueryData(['status-config'], saved);
      setSuccessMsg('Gespeichert');
      setErrorMsg('');
      setTimeout(() => setSuccessMsg(''), 2500);
    },
    onError: () => {
      setErrorMsg('Fehler beim Speichern');
    },
  });

  if (isLoading || !localConfig) {
    return <div className="animate-pulse h-40 bg-gray-100 rounded-lg" />;
  }

  const states = localConfig[activeTab];

  function updateState(index: number, patch: Partial<StateInfo>) {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const updated = [...prev[activeTab]];
      updated[index] = { ...updated[index], ...patch };
      return { ...prev, [activeTab]: updated };
    });
  }

  function removeState(index: number) {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const updated = prev[activeTab].filter((_, i) => i !== index);
      return { ...prev, [activeTab]: updated };
    });
  }

  function addState() {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const newEntry: StateInfo = {
        key: `neuer_status_${Date.now()}`,
        label: 'Neuer Status',
        color: 'gray',
        recommended_transitions: [],
      };
      return { ...prev, [activeTab]: [...prev[activeTab], newEntry] };
    });
  }

  function moveState(index: number, direction: -1 | 1) {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const arr = [...prev[activeTab]];
      const target = index + direction;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return { ...prev, [activeTab]: arr };
    });
  }

  return (
    <div>
      {/* Tabs */}
      <div className="flex border-b mb-6">
        {(['anfrage', 'inserat', 'verfahren'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {ENTITY_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Status list */}
      <div className="space-y-2">
        {states.map((entry, i) => (
          <div key={entry.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            {/* Order buttons */}
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => moveState(i, -1)}
                disabled={i === 0}
                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Nach oben"
              >
                ▲
              </button>
              <button
                onClick={() => moveState(i, 1)}
                disabled={i === states.length - 1}
                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Nach unten"
              >
                ▼
              </button>
            </div>

            {/* Color preview */}
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${COLOR_CLASSES[entry.color] ?? 'bg-gray-100 text-gray-600'}`}>
              {entry.label || entry.key}
            </span>

            {/* Key (readonly) */}
            <code className="text-xs text-gray-400 font-mono hidden sm:block">{entry.key}</code>

            {/* Label input */}
            <input
              type="text"
              value={entry.label}
              onChange={(e) => updateState(i, { label: e.target.value })}
              className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500"
              placeholder="Bezeichnung"
            />

            {/* Color selector */}
            <select
              value={entry.color}
              onChange={(e) => updateState(i, { color: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500"
            >
              {AVAILABLE_COLORS.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            {/* Remove button */}
            <button
              onClick={() => removeState(i)}
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              title="Entfernen"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Add + Save */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={addState}
          className="px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
        >
          + Status hinzufügen
        </button>
        <div className="flex items-center gap-3">
          {successMsg && <span className="text-sm text-green-600">{successMsg}</span>}
          {errorMsg && <span className="text-sm text-red-600">{errorMsg}</span>}
          <button
            onClick={() => saveMutation.mutate(localConfig)}
            disabled={saveMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saveMutation.isPending ? 'Speichert...' : saveLabel ?? 'Speichern'}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Hinweis: Status-Keys werden beim Hinzufügen automatisch generiert. Bestehende Einträge in der Datenbank behalten ihren Key.
      </p>
    </div>
  );
}
